import { useLayoutEffect, useState } from 'react'

type SpotBox = { x: number; y: number; w: number; h: number }

type SkillsSpotlightProps = {
  targetEl: HTMLElement | null
  onDismiss: () => void
}

export const SkillsSpotlight = ({ targetEl, onDismiss }: SkillsSpotlightProps) => {
  const [box, setBox] = useState<SpotBox | null>(null)

  useLayoutEffect(() => {
    if (!targetEl) {
      setBox(null)
      return
    }

    const measure = () => {
      const r = targetEl.getBoundingClientRect()
      if (r.width < 8 || r.height < 8) {
        setBox(null)
        return
      }
      setBox({
        x: r.left - 6,
        y: r.top - 6,
        w: r.width + 12,
        h: r.height + 12,
      })
    }

    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [targetEl])

  if (!targetEl || !box) return null

  const cardLeft = Math.max(16, Math.min(box.x, window.innerWidth - 312))
  const cardBottom = window.innerHeight - box.y + 12

  return (
    <div
      role="dialog"
      aria-label="Business Skills tip"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 700,
        pointerEvents: 'none',
        animation: 'bIn .3s ease both',
      }}
    >
      <div
        style={{
          position: 'fixed',
          left: box.x,
          top: box.y,
          width: box.w,
          height: box.h,
          boxShadow: '0 0 0 9999px rgba(15,30,56,0.62)',
          borderRadius: 12,
          border: '1.5px solid rgba(201,168,76,.7)',
          pointerEvents: 'none',
          animation: 'zapPulse 1.1s ease-in-out 2',
        }}
      />
      <div
        style={{
          position: 'fixed',
          left: cardLeft,
          bottom: cardBottom,
          width: 296,
          background: 'var(--white)',
          borderRadius: 16,
          boxShadow: '0 18px 50px rgba(0,0,0,0.3)',
          padding: '16px 17px 15px',
          animation: 'mIn .4s cubic-bezier(.16,1,.3,1) both',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              background: 'rgba(201,168,76,0.12)',
              border: '1px solid rgba(201,168,76,0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
            }}
          >
            ⚡
          </div>
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 14.5,
              fontWeight: 500,
              color: 'var(--navy)',
            }}
          >
            Skills live here
          </div>
        </div>
        <p
          style={{
            fontSize: 12.5,
            color: 'var(--muted)',
            lineHeight: 1.65,
            marginBottom: 12,
          }}
        >
          Tap a ⚡ skill anytime — or More for the full list. Athena works in that skill&apos;s
          format for your business.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            width: '100%',
            background: 'var(--navy)',
            color: 'var(--white)',
            border: 'none',
            borderRadius: 11,
            padding: 10,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </div>
    </div>
  )
}
